import * as THREE from 'three';

/**
 * Compute distance from boundary for each vertex using geodesic approximation
 * @param {THREE.BufferGeometry} geometry - Mesh geometry
 * @param {Array} boundaryVertices - Array of boundary vertex indices
 * @param {number} passes - Number of relaxation passes (default: 10)
 * @returns {Float32Array} Distance field array
 */
export function computeDistanceFromBoundary(geometry, boundaryVertices, passes = 10) {
  const positions = geometry.attributes.position;
  const vertexCount = positions.count;
  const distances = new Float32Array(vertexCount);

  // Initialize with large values
  distances.fill(Infinity);

  // Boundary vertices have distance 0
  boundaryVertices.forEach(idx => {
    distances[idx] = 0;
  });

  // Use geodesic distance approximation via multi-pass smoothing
  const index = geometry.index ? geometry.index.array : null;
  const triangleCount = index ? index.length / 3 : vertexCount / 3;

  for (let pass = 0; pass < passes; pass++) {
    const newDistances = distances.slice();

    // For each triangle, propagate distance through edges
    for (let t = 0; t < triangleCount; t++) {
      const i0 = index ? index[t * 3 + 0] : t * 3 + 0;
      const i1 = index ? index[t * 3 + 1] : t * 3 + 1;
      const i2 = index ? index[t * 3 + 2] : t * 3 + 2;

      const v0 = new THREE.Vector3().fromBufferAttribute(positions, i0);
      const v1 = new THREE.Vector3().fromBufferAttribute(positions, i1);
      const v2 = new THREE.Vector3().fromBufferAttribute(positions, i2);

      const d01 = v0.distanceTo(v1);
      const d12 = v1.distanceTo(v2);
      const d20 = v2.distanceTo(v0);

      newDistances[i0] = Math.min(newDistances[i0], distances[i1] + d01, distances[i2] + d20);
      newDistances[i1] = Math.min(newDistances[i1], distances[i0] + d01, distances[i2] + d12);
      newDistances[i2] = Math.min(newDistances[i2], distances[i0] + d20, distances[i1] + d12);
    }

    distances.set(newDistances);
  }

  return distances;
}

/**
 * Get maximum falloff distance from segment parameters
 * @param {Array} falloffHeightValues - Falloff height per segment (mm)
 * @param {Array} falloffDeflectValues - Falloff deflect per segment (mm)
 * @param {number} mmToUnit - Conversion factor (default: 0.1)
 * @returns {number} Maximum falloff distance in scene units
 */
export function getMaxFalloffDistance(falloffHeightValues, falloffDeflectValues, mmToUnit = 0.1) {
  const maxFalloff = Math.max(
    ...falloffHeightValues,
    ...falloffDeflectValues
  ) * mmToUnit;

  return maxFalloff;
}
